import React from 'react';
import { presetData, ventOptions, findVentOption } from '../data';
import { ventTypeLabel, ventTypePlaceholder } from '../ventTypeLabels';

const Output = ({
    items,
    showComponentList,
    onPresetSelect,
    payloadText,
    setPayloadText,
    outputError,
    outputStatus,
    selectedItemIndex,
    setSelectedItemIndex,
    handleDiametersChange,
    handleHeightChange,
    handleAmbientTempChange,
    handleOrientationChange,
    handleVentTypeChange,
    availableOrientations = [
      { value: 'vertical',   label: 'Vertical' },
      { value: 'horizontal', label: 'Horizontal' },
    ],
    handleSequenceOrderChange,
    handleDelete,
    handleClearDesign,
    onFinalizeDesign,
    finalizeDisabled = false,
    finalizeTitle = '',
    isCalculating = false,
    setShowComponentList,
}) => {
    const selectedItem = selectedItemIndex !== null && selectedItemIndex !== undefined ? items[selectedItemIndex] : null;
    const canEditHeight = selectedItem?.type === 'Pipe';
    const canEditOrientation =
        selectedItem?.componentType === 'pipe-straight' ||
        selectedItem?.componentType === 'chimney-straight' ||
        selectedItem?.componentType === 'masonry-straight';
    const canEditVentType = canEditOrientation;
    // Filter the catalog to match the selected part's category — stove pipe
    // parts see only stove pipe options; chimney parts see only chimney options;
    // masonry parts see only lined-masonry options.
    const isChimneyPart = selectedItem?.componentType === 'chimney-straight';
    const isStovePipePart = selectedItem?.componentType === 'pipe-straight';
    const isMasonryPart = selectedItem?.componentType === 'masonry-straight';
    const stovePipeOptions = isStovePipePart
        ? ventOptions.filter(v => v.category === 'stove_pipe')
        : [];
    const chimneyOptions = isChimneyPart
        ? ventOptions.filter(v => v.category === 'chimney')
        : [];
    const masonryOptions = isMasonryPart
        ? ventOptions.filter(v => v.category === 'masonry')
        : [];
    const selectedVentOption = selectedItem?.ventOptionId
        ? findVentOption(selectedItem.ventOptionId)
        : null;

    const selectedTypeLabel = (() => {
        if (!selectedItem) return '';
        const ct = selectedItem.componentType;
        if (ct === 'cap')              return 'Cap';
        if (ct === 'pipe-straight')    return 'Stove Pipe';
        if (ct === 'chimney-straight') return 'Chimney';
        if (ct === 'masonry-straight') return 'Masonry Chimney';
        if (ct === 'masonry-tee')      return 'Masonry Tee';
        if (ct === 'tee')              return 'Stove Pipe Tee';
        if (ct === 'chimney-tee')      return 'Chimney Tee';
        if (ct === 'elbow-90')         return 'Stove Pipe Elbow (90°)';
        if (ct === 'elbow-45')         return 'Stove Pipe Elbow (45°)';
        if (ct === 'chimney-30')       return 'Chimney Elbow (30°)';
        return selectedItem.name || ct || 'Part';
    })();

    return (
        <div className="output-container">
            {selectedItem && (
                <div className="pipe-info-sidebar">
                    <div className="pipe-info-header">
                        <strong>{selectedTypeLabel}</strong>
                        <button
                            className='close-btn'
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedItemIndex && setSelectedItemIndex(null);
                            }}
                        >×</button>
                    </div>
                    <div className="pipe-info-body">
                        {canEditVentType && (
                            <div className="info-row">
                                <label>{ventTypeLabel(selectedItem.componentType)}</label>
                                <select
                                    id={`vent-type-${selectedItemIndex}`}
                                    name="ventType"
                                    value={selectedItem.ventOptionId || ''}
                                    onChange={(e) => handleVentTypeChange && handleVentTypeChange(selectedItemIndex, e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <option value="">{ventTypePlaceholder(selectedItem.componentType)}</option>
                                    {stovePipeOptions.map(v => (
                                        <option key={v.id} value={v.id}>{v.name}</option>
                                    ))}
                                    {chimneyOptions.map(v => (
                                        <option key={v.id} value={v.id}>{v.name}</option>
                                    ))}
                                    {masonryOptions.map(v => (
                                        <option key={v.id} value={v.id}>{v.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        {selectedVentOption && (
                            <div className="info-row info-row-description">
                                <small style={{ color: 'var(--border-subtle)', fontStyle: 'italic', lineHeight: 1.3 }}>
                                    {selectedVentOption.description}
                                </small>
                            </div>
                        )}
                        <div className="info-row">
                            <label>Diameter</label>
                            <select
                                id={`diameter-${selectedItemIndex}`}
                                name="diameter"
                                onChange={(e) => handleDiametersChange(selectedItemIndex, e.target.selectedOptions)}
                                value={selectedItem.selectedDiameter || selectedItem.diameters?.[0] || '6'}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <option value="3">3 in</option>
                                <option value="4">4 in</option>
                                <option value="5">5 in</option>
                                <option value="6">6 in</option>
                                <option value="7">7 in</option>
                                <option value="8">8 in</option>
                            </select>
                        </div>
                        {canEditHeight && (
                            <div className="info-row">
                                <label>Length</label>
                                <input
                                    type="number"
                                    id={`height-${selectedItemIndex}`}
                                    name="height"
                                    min="0.5"
                                    step="0.25"
                                    value={selectedItem.length ?? selectedItem.height ?? 3}
                                    onChange={(e) => handleHeightChange(selectedItemIndex, e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ width: '70px' }}
                                />
                                <span style={{ marginLeft: '4px' }}>ft</span>
                            </div>
                        )}
                        {canEditOrientation && (() => {
                            const curOrient = selectedItem.orientation || 'vertical';
                            const nextOrient = curOrient === 'vertical' ? 'horizontal' : 'vertical';
                            const curLabel = (availableOrientations.find(o => o.value === curOrient) || { label: curOrient }).label;
                            return (
                                <div className="info-row">
                                    <label>Orientation</label>
                                    <button
                                        type="button"
                                        id={`orientation-${selectedItemIndex}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOrientationChange(selectedItemIndex, nextOrient);
                                        }}
                                        title="Click to rotate 90°"
                                    >
                                        ↻ {curLabel}
                                    </button>
                                </div>
                            );
                        })()}
                        <div className="info-row">
                            <label>Surrounding Temp</label>
                            <input
                                type="number"
                                value={selectedItem.ambientTempF ?? ''}
                                onChange={(e) => handleAmbientTempChange(selectedItemIndex, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                            /> <span>°F</span>
                        </div>
                        <div className="info-row">
                            <label>Part Number</label>
                            <input
                                type="number"
                                min="1"
                                step="1"
                                value={selectedItem.sequenceOrder ?? ''}
                                onChange={(e) => handleSequenceOrderChange(selectedItemIndex, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                style={{ width: '70px' }}
                            />
                        </div>
                        <button
                            className='delete-component-btn'
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(selectedItemIndex);
                            }}
                        >Delete Component</button>
                    </div>
                </div>
            )}

            <div className="design-actions">
                <button
                    className="finalize-design-btn"
                    title={finalizeTitle}
                    onClick={() => {
                        if (typeof onFinalizeDesign === 'function') {
                            onFinalizeDesign();
                        } else if (typeof setShowComponentList === 'function') {
                            setShowComponentList(true);
                        }
                    }}
                    disabled={finalizeDisabled}
                >
                    Finalize Design
                </button>

                <button
                    className="clear-design-btn"
                    onClick={handleClearDesign}
                    disabled={items.length === 0 || isCalculating}
                >
                    Clear Design
                </button>
            </div>

            {showComponentList && items.length > 0 && (
                <div className="component-list">
                    <h3>Component List</h3>
                    {items.map((item, index) => (
                        <div key={index} className="component-list-item">
                            <strong>{index + 1}. {item.name}</strong>
                            <div className="component-details">
                                <span>Type: {item.type || item.wallType || 'Single'}</span>
                                <span>Diameter: {item.selectedDiameter || item.diameters?.[0] || '6'} in</span>
                                <span>Orientation: {item.orientation || 'vertical'}</span>
                                {item.type !== 'Elbow' && <span>Length: {item.length ?? item.height ?? 3} ft</span>}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="calculate-section">
                {presetData.length > 0 && (
                    <div className="preset-section">
                        {presetData.map((preset, idx) => (
                            <button
                                hidden={true}
                                key={idx}
                                className="preset-btn"
                                title={preset.description}
                                onClick={() => {
                                    if (onPresetSelect) onPresetSelect(preset.config);
                                }}
                            >
                                {preset.name}
                            </button>
                        ))}
                    </div>
                )}

                <textarea
                    hidden={true}
                    className="payload-input"
                    rows={6}
                    placeholder='Enter JSON payload, e.g. {"key": "value"}'
                    value={payloadText}
                    onChange={(e) => setPayloadText(e.target.value)}
                    style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px', resize: 'vertical', boxSizing: 'border-box' }}
                />

                {outputStatus && (
                    <div className="result-error" style={{ borderColor: 'var(--action-blue)', background: '#eff6ff' }}>
                        <span className="result-fail" style={{ color: '#1d4ed8' }}>⏳ Status</span>
                        <pre className="result-error-text" style={{ color: '#1e3a8a' }}>{outputStatus}</pre>
                    </div>
                )}

                {outputError && (
                    <div className="result-error">
                        <span className="result-fail">&#10007; Error</span>
                        <pre className="result-error-text">{outputError}</pre>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Output;
